import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, CheckCheck } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button";
import { Drawer } from "@/shared/ui/drawer";
import { Skeleton } from "@/shared/ui/skeleton";

import { useListNotifications } from "../api/use-list-notifications";
import { useMarkAllRead } from "../api/use-notification-mutations";
import type { NotificationEvent } from "../lib/notif";

import { NotificationCard } from "./NotificationCard";

type DrawerFilter = "all" | "unread" | "alerts";

interface BellDrawerProps {
  open: boolean;
  onClose: () => void;
}

const MAX_VISIBLE = 6;

function applyFilter(items: NotificationEvent[], filter: DrawerFilter): NotificationEvent[] {
  switch (filter) {
    case "unread":
      return items.filter((n) => !n.read);
    case "alerts":
      return items.filter((n) => n.severity !== "P3");
    default:
      return items;
  }
}

export function BellDrawer({ open, onClose }: BellDrawerProps): JSX.Element {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<DrawerFilter>("all");

  const { data, isLoading, isError } = useListNotifications();
  const markAllRead = useMarkAllRead();
  const canWrite = useCan("notifications:write");

  const allItems = data?.items ?? [];
  const unreadCount = data?.unread_count ?? 0;
  // API returns newest-first (created_at DESC), so slice(0, MAX_VISIBLE) shows the most-recent items.
  const filtered = applyFilter(allItems, activeFilter).slice(0, MAX_VISIBLE);

  const filterChips: { key: DrawerFilter; label: string }[] = [
    { key: "all", label: t("filter.all") },
    { key: "unread", label: t("filter.unread") },
    { key: "alerts", label: t("filter.alerts") },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("page.title")}
      subtitle={unreadCount > 0 ? t("page.unread_count", { count: unreadCount }) : undefined}
      actions={
        canWrite ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid="bell-mark-all"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadCount === 0}
          >
            {t("action.mark_all_read")}
          </Button>
        ) : null
      }
    >
      <div data-testid="bell-drawer" className="flex h-full flex-col">
        {/* Filter chips */}
        <div className="flex gap-1.5 border-b border-[var(--premium-border)] px-4 py-2.5">
          {filterChips.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              data-testid={`bell-filter-${key}`}
              aria-pressed={activeFilter === key}
              onClick={() => setActiveFilter(key)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-ring",
                activeFilter === key
                  ? "bg-[var(--brand-600)] text-white shadow-[var(--sh-xs)]"
                  : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex flex-col gap-2.5 px-4 py-3">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          )}

          {isError && !isLoading && (
            <p className="px-4 py-6 text-sm text-[var(--intent-danger-fg)]">{t("state.error")}</p>
          )}

          {!isLoading && !isError && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--intent-success-bg)] text-[var(--intent-success-fg)]">
                <CheckCheck aria-hidden className="h-6 w-6" />
              </span>
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                {t("page.all_caught_up")}
              </p>
              <p className="text-xs text-[var(--text-subtle)]">{t("state.empty")}</p>
            </div>
          )}

          {!isLoading &&
            !isError &&
            filtered.map((event) => (
              <NotificationCard
                key={event.id}
                event={event}
                variant="compact"
                onNavigate={onClose}
              />
            ))}
        </div>

        {/* Footer: view all */}
        <div className="border-t border-[var(--premium-border)] p-3">
          <button
            type="button"
            data-testid="bell-view-all"
            className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-semibold text-[var(--brand-600)] transition-colors hover:bg-[var(--surface-sunken)] focus-ring"
            onClick={() => {
              onClose();
              void navigate({ to: "/notifications" });
            }}
          >
            {t("action.view_all")}
            <ArrowRight aria-hidden className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Drawer>
  );
}
